import { ReactNode } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { MFAReminderProvider } from "@/contexts/MFAReminderContext";
import { MusicProvider } from "@/contexts/MusicContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  CACHE_TIMES,
  REFETCH_CONFIG,
  PERFORMANCE_CONFIG,
} from "@/hooks/cache/cacheConfig";
import { PWAOfflineWrapper } from "@/components/pwa/PWAOfflineWrapper";
import { PWACacheProvider } from "@/components/pwa/PWACacheProvider";

// Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Use optimized cache settings
      staleTime: CACHE_TIMES.DYNAMIC.staleTime,
      gcTime: CACHE_TIMES.DYNAMIC.gcTime,

      // Optimized refetch settings
      ...REFETCH_CONFIG.STANDARD,

      // Performance optimizations
      retry: PERFORMANCE_CONFIG.retry.attempts,
      retryDelay: PERFORMANCE_CONFIG.retry.delay,
      networkMode: PERFORMANCE_CONFIG.networkMode,
      throwOnError: PERFORMANCE_CONFIG.throwOnError,

      // Enable background updates but not on window focus for better performance
      refetchOnWindowFocus: false,

      // Reduce network calls
      refetchInterval: false,
    },
    mutations: {
      // Optimized mutation settings
      retry: 1,
      networkMode: PERFORMANCE_CONFIG.networkMode,
      throwOnError: PERFORMANCE_CONFIG.throwOnError,
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <NextThemesProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem={false}
        themes={["dark", "light", "custom"]}
      >
        <ThemeProvider>
          <MFAReminderProvider>
            <AuthProvider>
              <MusicProvider>
                <Router>
                  <PWACacheProvider>
                    <PWAOfflineWrapper>{children}</PWAOfflineWrapper>
                  </PWACacheProvider>
                </Router>
              </MusicProvider>
            </AuthProvider>
          </MFAReminderProvider>
        </ThemeProvider>
      </NextThemesProvider>
    </QueryClientProvider>
  );
}
